// Espelha a validação do backend em common/helpers/MediaAttachmentHelper.php.
// As mesmas regras (limites de mídia do WhatsApp) são aplicadas no envio de
// mídia pelo omnichannel e no upload de arquivos para o repositório interno,
// para dar feedback imediato ao usuário antes da requisição ao backend.

const CATEGORY_MIME_TYPES = {
  image: ["image/jpeg", "image/png"],
  video: ["video/mp4", "video/3gpp"],
  audio: ["audio/aac", "audio/mp4", "audio/mpeg", "audio/amr", "audio/ogg"],
  document: [
    "application/pdf",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "application/vnd.ms-excel",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "application/vnd.ms-powerpoint",
    "application/vnd.openxmlformats-officedocument.presentationml.presentation",
    "text/plain",
  ],
};

const MEGABYTE = 1024 * 1024;

const CATEGORY_MAX_SIZES = {
  image: 5 * MEGABYTE,
  video: 16 * MEGABYTE,
  audio: 16 * MEGABYTE,
  document: 100 * MEGABYTE,
};

const CATEGORY_LABELS = {
  image: "imagem",
  video: "vídeo",
  audio: "áudio",
  document: "documento",
};

export function resolveMediaCategory(mimeType) {
  const categories = Object.keys(CATEGORY_MIME_TYPES);

  for (const category of categories) {
    if (CATEGORY_MIME_TYPES[category].includes(mimeType)) return category;
  }

  return null;
}

// Valida um único arquivo (tipo e tamanho). Retorna { valid, reason }.
// Quando expectedCategory é informada (image/video/audio/document), o arquivo
// também precisa pertencer a essa categoria — usado, por exemplo, na mídia de
// header de template, cujo tipo (imagem/vídeo/documento) é fixado pelo template.
export function validateMediaFile(file, expectedCategory = null) {
  const mimeType = file.type;
  const category = resolveMediaCategory(mimeType);

  if (!category) {
    return {
      valid: false,
      reason: `O arquivo "${file.name}" tem um tipo (${mimeType || "desconhecido"}) que não é suportado para envio.`,
    };
  }

  if (expectedCategory && category !== expectedCategory) {
    return {
      valid: false,
      reason: `O arquivo "${file.name}" não corresponde ao tipo esperado (${CATEGORY_LABELS[expectedCategory]}).`,
    };
  }

  const maxSize = CATEGORY_MAX_SIZES[category];

  if (file.size > maxSize) {
    return {
      valid: false,
      reason: `O arquivo "${file.name}" excede o tamanho máximo permitido para ${CATEGORY_LABELS[category]} (${maxSize / MEGABYTE}MB).`,
    };
  }

  return { valid: true, category };
}

// Particiona uma lista de arquivos entre válidos e inválidos (tipo e tamanho).
// Permite o envio parcial: os arquivos válidos seguem adiante enquanto os
// inválidos são apenas reportados ao usuário, sem bloquear o lote inteiro.
export function partitionMediaFiles(files) {
  const fileList = Array.from(files);
  const validFiles = [];
  const invalidFiles = [];
  const errors = [];

  for (const file of fileList) {
    const result = validateMediaFile(file);

    if (result.valid) {
      validFiles.push(file);
    } else {
      invalidFiles.push(file);
      errors.push(result.reason);
    }
  }

  return { validFiles, invalidFiles, errors };
}

// Lista de MIMEs aceitos, para o atributo accept dos inputs de arquivo (faz o
// seletor nativo do sistema já pré-filtrar os tipos suportados).
export const MEDIA_ACCEPT_ATTRIBUTE = Object.values(CATEGORY_MIME_TYPES).flat().join(",");

// Resumo legível dos formatos e limites aceitos, para exibir ao usuário nas
// áreas de upload/arrasto e manter a comunicação alinhada às regras acima.
export function getMediaLimitsSummary() {
  return "Imagens JPG/PNG (até 5MB), vídeos MP4/3GPP (até 16MB), áudios AAC/MP3/OGG/AMR (até 16MB) e documentos PDF/DOC/XLS/PPT/TXT (até 100MB).";
}
